/**
 * 入力サニタイゼーション・XSS対策ユーティリティ
 * セキュリティ強化のためのユーザー入力処理
 */

/**
 * 基本的なHTMLエスケープ
 * XSS攻撃を防ぐために危険な文字をエスケープ
 */
export const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };
  
  return text.replace(/[&<>"'`=/]/g, (s) => map[s]);
};

/**
 * スクリプトタグとイベントハンドラーを除去
 * より厳格なXSS対策
 */
export const removeScripts = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/expression\s*\(/gi, '');
};

/**
 * 店舗名用サニタイゼーション
 * 基本的な特殊文字のみ許可
 */
export const sanitizeStoreName = (name: string): string => {
  if (typeof name !== 'string') {
    return '';
  }
  
  // 基本的な文字、数字、ひらがな、カタカナ、漢字、基本的な記号のみ許可
  const cleanName = name.replace(/[^\w\s\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\-.,:!?]/g, '');
  
  return escapeHtml(cleanName.trim());
};

/**
 * メールアドレス用サニタイゼーション
 * RFC 5322準拠の基本的な検証とサニタイゼーション
 */
export const sanitizeEmail = (email: string): string => {
  if (typeof email !== 'string') {
    return '';
  }
  
  // 基本的なメールアドレス形式チェック
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const cleanEmail = email.trim().toLowerCase();
  
  if (!emailRegex.test(cleanEmail)) {
    return '';
  }
  
  return escapeHtml(cleanEmail);
};

/**
 * 電話番号用サニタイゼーション
 * 数字、ハイフン、括弧のみ許可
 */
export const sanitizePhoneNumber = (phone: string): string => {
  if (typeof phone !== 'string') {
    return '';
  }
  
  // 数字、ハイフン、括弧、プラス記号のみ許可
  const cleanPhone = phone.replace(/[^\d\-()+\s]/g, '');
  
  return escapeHtml(cleanPhone.trim());
};

/**
 * 住所用サニタイゼーション
 * 基本的な住所に使用される文字のみ許可
 */
export const sanitizeAddress = (address: string): string => {
  if (typeof address !== 'string') {
    return '';
  }
  
  // 文字、数字、ひらがな、カタカナ、漢字、住所記号のみ許可
  const cleanAddress = address.replace(/[^\w\s\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\-.,()]/g, '');
  
  return escapeHtml(cleanAddress.trim());
};

/**
 * 一般的なテキストフィールド用サニタイゼーション
 * 基本的なXSS対策を適用
 */
export const sanitizeText = (text: string, maxLength = 1000): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  let sanitized = removeScripts(text);
  sanitized = escapeHtml(sanitized);
  
  // 最大長制限
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized.trim();
};

/**
 * URLの基本的な検証とサニタイゼーション
 * HTTPSまたはHTTPのみ許可
 */
export const sanitizeUrl = (url: string): string => {
  if (typeof url !== 'string') {
    return '';
  }
  
  const cleanUrl = url.trim();
  
  // 基本的なURL形式チェック（https, httpのみ許可）
  const urlRegex = /^https?:\/\/[^\s$.?#].[^\s]*$/i;
  
  if (!urlRegex.test(cleanUrl)) {
    return '';
  }
  
  return escapeHtml(cleanUrl);
};

/**
 * 数値用サニタイゼーション
 * 数値以外の文字を除去
 */
export const sanitizeNumber = (value: string | number): number => {
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }
  
  if (typeof value !== 'string') {
    return 0;
  }
  
  const numericOnly = value.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(numericOnly);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * バリデーション結果インターフェース
 */
export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  errors: string[];
}

/**
 * 包括的な入力バリデーション
 * 複数のバリデーションルールを適用
 */
export const validateAndSanitize = (
  value: string,
  rules: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    type?: 'email' | 'phone' | 'url' | 'text' | 'storeName' | 'address';
  }
): ValidationResult => {
  const errors: string[] = [];
  let sanitizedValue = '';
  
  // 型別サニタイゼーション
  switch (rules.type) {
    case 'email':
      sanitizedValue = sanitizeEmail(value);
      break;
    case 'phone':
      sanitizedValue = sanitizePhoneNumber(value);
      break;
    case 'url':
      sanitizedValue = sanitizeUrl(value);
      break;
    case 'storeName':
      sanitizedValue = sanitizeStoreName(value);
      break;
    case 'address':
      sanitizedValue = sanitizeAddress(value);
      break;
    default:
      sanitizedValue = sanitizeText(value, rules.maxLength);
  }
  
  // 必須チェック
  if (rules.required && !sanitizedValue) {
    errors.push('この項目は必須です');
  }
  
  // 長さチェック
  if (rules.minLength && sanitizedValue.length < rules.minLength) {
    errors.push(`最低${rules.minLength}文字以上入力してください`);
  }
  
  if (rules.maxLength && sanitizedValue.length > rules.maxLength) {
    errors.push(`${rules.maxLength}文字以内で入力してください`);
  }
  
  // パターンチェック
  if (rules.pattern && !rules.pattern.test(sanitizedValue)) {
    errors.push('正しい形式で入力してください');
  }
  
  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors
  };
};

export default {
  escapeHtml,
  removeScripts,
  sanitizeStoreName,
  sanitizeEmail,
  sanitizePhoneNumber,
  sanitizeAddress,
  sanitizeText,
  sanitizeUrl,
  sanitizeNumber,
  validateAndSanitize
};